import React, { useState, useEffect, useRef } from 'react';
import { Modal, List, Typography, Row, Col, Card } from 'antd';
import { DndContext, closestCenter, PointerSensor, TouchSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { createPortal } from 'react-dom';
import { Image } from 'antd';
import {
    greyScaleTokens,
    variantTokens,
} from '../../../theme/theme';
import { PrimaryButton, OutlineButton, ErrorButton } from '../../Components/Buttons/Buttons';

const { Text, Title, Paragraph } = Typography;

// Componente Tutorial per la sezione sticky
const Tutorial = ({ title, description, children }) => (
    <Card
        style={{
            position: 'sticky',
            top: 0,
            height: 'fit-content',
            maxHeight: '100vh',
            boxShadow: 'none',
            background: greyScaleTokens.grey100,
            overflowY: 'hidden',
            zIndex: 1,
            paddingBottom: '1.5rem' // extra space for scroll
        }}
    >
        <Title level={3} style={{ marginBottom: '1rem' }}>{title}</Title>
        <Paragraph style={{ fontSize: '1rem' }}>{description}</Paragraph>
        {children}
    </Card>
);

// Componente modale di conferma per discard
const ConfirmDiscardModal = ({ visible, onConfirm, onCancel, mode }) => {
    const isVariantMode = mode === 'variants';
    const itemType = isVariantMode ? 'variant' : 'product';

    return (
        <Modal
            title="Discard changes?"
            open={visible}
            onCancel={onCancel}
            centered={true}
            footer={[
                <OutlineButton
                    key="cancel"
                    onClick={onCancel}
                    style={{ marginRight: '0.5rem' }}
                >
                    Stay
                </OutlineButton>,
                <ErrorButton
                    key="confirm"
                    onClick={onConfirm}
                >
                    Discard
                </ErrorButton>,
            ]}
            width="25rem"
        >
            <Text>You have unsaved {itemType} order changes. Are you sure you want to discard them?</Text>
        </Modal>
    );
};

// Componente modale di conferma per rearrange
const ConfirmRearrangeModal = ({ visible, onConfirm, onCancel, loading, mode }) => {
    const isVariantMode = mode === 'variants';
    const itemType = isVariantMode ? 'variants' : 'products';
    const contextText = isVariantMode
        ? 'This action will update the priority order of all variants for this product.'
        : 'This action will update the priority order of all items in the catalogue.';

    return (
        <Modal
            title={`Confirm ${isVariantMode ? 'Variant' : 'Product'} Rearrangement`}
            open={visible}
            onCancel={onCancel}
            centered={true}
            footer={[
                <OutlineButton
                    key="cancel"
                    onClick={onCancel}
                    style={{ marginRight: '0.5rem' }}
                    disabled={loading}
                >
                    Cancel
                </OutlineButton>,
                <PrimaryButton
                    key="confirm"
                    onClick={onConfirm}
                    loading={loading}
                >
                    Confirm Rearrangement
                </PrimaryButton>,
            ]}
            width="31.25rem"
        >
            <Text>Are you sure you want to save this new {itemType} arrangement? {contextText}</Text>
        </Modal>
    );
};

// Creiamo un elemento DOM esterno per il portale una sola volta (per lo zoom)
let portalRoot = null;
if (typeof window !== 'undefined') {
    // Verifica se il portale esiste già
    portalRoot = document.getElementById('zoom-portal-root');

    // Se non esiste, crealo
    if (!portalRoot) {
        portalRoot = document.createElement('div');
        portalRoot.id = 'zoom-portal-root';
        portalRoot.style.position = 'fixed';
        portalRoot.style.top = '0';
        portalRoot.style.left = '0';
        portalRoot.style.width = '100%';
        portalRoot.style.height = '100%';
        portalRoot.style.pointerEvents = 'none';
        portalRoot.style.zIndex = '2147483647'; // Il massimo zIndex possibile
        document.body.appendChild(portalRoot);
    }
}

// Componente per un singolo elemento draggabile
const SortableItem = ({ item, index }) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
    } = useSortable({ id: item.id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        padding: '0.5rem 1rem',
        border: `1px solid ${greyScaleTokens.grey300}`,
        borderRadius: '0.5rem',
        marginBottom: '0.5rem',
        backgroundColor: variantTokens.white,
        display: 'flex',
        alignItems: 'center',
        cursor: 'grab',
        height: '3rem', // Altezza ridotta per mostrare più prodotti
        gap: '1.5rem', // Spazio uniforme tra tutti gli elementi
    };

    // Stato per gestire lo zoom dell'immagine
    const [isHovered, setIsHovered] = useState(false);
    const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

    // Funzione per gestire il movimento del mouse
    const handleMouseMove = (e) => {
        setMousePosition({
            x: e.clientX,
            y: e.clientY
        });
    };

    // Stile per l'immagine di base
    const imgStyle = {
        maxHeight: '100%',
        maxWidth: '100%',
        objectFit: 'contain',
        cursor: 'zoom-in'
    };

    return (
        <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
            {/* Drag Handle */}
            <DragIndicatorIcon style={{ color: greyScaleTokens.grey500, flexShrink: 0 }} />

            {/* Index - più evidente */}
            <div style={{
                minWidth: '1.5rem',
                height: '1.5rem',
                backgroundColor: greyScaleTokens.grey200,
                borderRadius: '0.25rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
            }}>
                <Text style={{
                    color: greyScaleTokens.grey700,
                    fontFamily: 'monospace',
                    fontWeight: 'bold',
                    fontSize: '0.75rem'
                }}>
                    {index + 1}
                </Text>
            </div>

            {/* Image */}
            <div
                style={{
                    width: '3rem',
                    height: '3rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'transparent',
                    borderRadius: '0.25rem',
                    flexShrink: 0,
                    position: 'relative',
                }}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
                onMouseMove={handleMouseMove}
            >
                {item.imgUrl ? (
                    <>
                        <img
                            src={item.imgUrl}
                            alt={item.modelName}
                            style={imgStyle}
                        />
                        {/* Zoom overlay usando portal */}
                        {isHovered && portalRoot && createPortal(
                            <div
                                style={{
                                    position: 'fixed',
                                    top: mousePosition.y < window.innerHeight / 2 ? mousePosition.y + 20 : mousePosition.y - 320,
                                    left: mousePosition.x < window.innerWidth / 2 ? mousePosition.x + 20 : mousePosition.x - 320,
                                    backgroundColor: variantTokens.white,
                                    padding: '0.625rem',
                                    borderRadius: '0.5rem',
                                    boxShadow: '0 0.25rem 1.25rem rgba(0, 0, 0, 0.3)',
                                    pointerEvents: 'none'
                                }}
                            >
                                <Image
                                    src={item.imgUrl}
                                    alt={item.modelName}
                                    preview={false}
                                    width={300}
                                    height={300}
                                    style={{ objectFit: 'contain' }}
                                />
                            </div>,
                            portalRoot
                        )}
                    </>
                ) : (
                    <Text
                        type="secondary"
                        style={{
                            fontSize: '0.625rem',
                            textAlign: 'center',
                            color: greyScaleTokens.grey500
                        }}
                    >
                        No Img
                    </Text>
                )}
            </div>

            {/* Details */}
            <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.125rem' }}>
                    <Text
                        strong
                        style={{
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            color: variantTokens.black,
                            fontSize: '0.875rem',
                            lineHeight: '1.2'
                        }}
                    >
                        {item.modelName}
                    </Text>
                    {item.brandName && (
                        <Text
                            type="secondary"
                            style={{
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                color: greyScaleTokens.grey600,
                                fontSize: '0.75rem',
                                lineHeight: '1.2'
                            }}
                        >
                            {item.brandName}
                        </Text>
                    )}
                </div>
            </div>
        </div>
    );
};


const ReorderModal = ({ visible, onCancel, onSave, items, loading, mode = 'products' }) => {
    const [activeItems, setActiveItems] = useState([]);
    const [showConfirmDiscard, setShowConfirmDiscard] = useState(false);
    const [showConfirmRearrange, setShowConfirmRearrange] = useState(false);
    const hasChanges = useRef(false);

    // Configurazione dei sensori per dnd-kit
    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(TouchSensor)
    );

    useEffect(() => {
        // Inizializza la lista quando la modale diventa visibile o gli item cambiano
        if (visible) {
            setActiveItems(items);
            hasChanges.current = false; // Resetta lo stato delle modifiche all'apertura
        }
    }, [items, visible]);

    const handleDragEnd = (event) => {
        const { active, over } = event;

        // Controllo di sicurezza per evitare l'errore "Cannot read properties of null"
        if (!active || !over || active.id === over.id) {
            return;
        }

        hasChanges.current = true; // Segna che sono state fatte delle modifiche
        setActiveItems((currentItems) => {
            const oldIndex = currentItems.findIndex((item) => item.id === active.id);
            const newIndex = currentItems.findIndex((item) => item.id === over.id);
            return arrayMove(currentItems, oldIndex, newIndex);
        });
    };

    // Funzione da chiamare quando si clicca sul pulsante di salvataggio
    const handleSave = () => {
        if (hasChanges.current) {
            setShowConfirmRearrange(true);
        } else {
            // Se non ci sono modifiche, chiudi direttamente
            onCancel();
        }
    };

    // Gestore della chiusura per controllare se ci sono modifiche non salvate
    const handleCancel = () => {
        if (hasChanges.current) {
            setShowConfirmDiscard(true);
        } else {
            onCancel(); // Se non ci sono modifiche, chiudi direttamente
        }
    };

    // Conferma il discard delle modifiche
    const handleConfirmDiscard = () => {
        setShowConfirmDiscard(false);
        onCancel();
    };

    // Annulla il discard
    const handleCancelDiscard = () => {
        setShowConfirmDiscard(false);
    };

    // Conferma il rearrange
    const handleConfirmRearrange = async () => {
        try {
            await onSave(activeItems);
            // Dopo il salvataggio, chiudi la modale di conferma e resetta lo stato
            setShowConfirmRearrange(false);
            hasChanges.current = false;
            // Chiudi anche la modale principale
            onCancel();
        } catch (error) {
            // In caso di errore, mantieni la modale di conferma aperta
            console.error('Error during rearrange:', error);
        }
    };

    // Annulla il rearrange
    const handleCancelRearrange = () => {
        setShowConfirmRearrange(false);
    };

    return (
        <>
            <Modal
                title={
                    <Typography.Title level={4} style={{ marginBottom: '1rem' }}>
                        Rearrange {mode === 'variants' ? 'Variants' : 'Products'} by Priority
                    </Typography.Title>
                }
                open={visible && !showConfirmRearrange}
                onCancel={handleCancel}
                width="85vw"
                centered={true}
                maskClosable={false}
                footer={null}
                destroyOnHidden={true}
            >
                <Row gutter={32} style={{ minHeight: '25rem' }}>
                    {/* Left: Drag and Drop List */}
                    <Col xs={24} md={12}>
                        <div style={{ padding: '1.25rem 1rem' }}>
                            <DndContext
                                sensors={sensors}
                                collisionDetection={closestCenter}
                                onDragEnd={handleDragEnd}
                            >
                                <SortableContext
                                    items={activeItems.map(i => i.id)}
                                    strategy={verticalListSortingStrategy}
                                >
                                    <List
                                        dataSource={activeItems}
                                        renderItem={(item, index) => (
                                            <SortableItem key={item.id} item={item} index={index} />
                                        )}
                                        style={{
                                            padding: '0.0625rem',
                                            borderRadius: '0.25rem',
                                            maxHeight: '60vh',
                                            overflowY: 'auto'
                                        }}
                                    />
                                </SortableContext>
                            </DndContext>

                        </div>
                    </Col>

                    {/* Right: Tutorial */}
                    <Col xs={24} md={12} style={{ display: 'flex', flexDirection: 'column', justifyContent: 'start', paddingTop: '1.5rem', paddingRight: '2rem' }}>
                        <Tutorial
                            title={`${mode === 'variants' ? 'Variant' : 'Product'} Priority Rearrangement`}
                            description={
                                <>
                                    Drag and drop {mode === 'variants' ? 'variants' : 'items'} to change their display order {mode === 'variants' ? 'for this product' : 'in the catalogue'}. Items at the top will appear first to your customers.<br /><br />

                                    <b>How to use:</b>
                                    <ul style={{ paddingLeft: 20, margin: '8px 0' }}>
                                        <li><b>Drag:</b> Click and hold the drag handle (⋮⋮) to move {mode === 'variants' ? 'variants' : 'items'}</li>
                                        <li><b>Reorder:</b> Drop {mode === 'variants' ? 'variants' : 'items'} in their new position</li>
                                        <li><b>Preview:</b> Hover over {mode === 'variants' ? 'variant' : 'product'} images to see a larger preview</li>
                                        <li><b>Priority:</b> Numbers show the new display order (1 = first)</li>
                                    </ul>

                                    <b>Tips:</b>
                                    <ul style={{ paddingLeft: 20, margin: 0 }}>
                                        <li>Place your most important or popular {mode === 'variants' ? 'variants' : 'items'} at the top</li>
                                        <li>Consider {mode === 'variants' ? 'best-selling colors/styles' : 'seasonal or promotional products'} for higher priority</li>
                                        <li>Changes will be applied to the {mode === 'variants' ? 'product variants' : 'live catalogue'} after confirmation</li>
                                        <li>You can cancel at any time to discard changes</li>
                                    </ul>
                                </>
                            }
                        />
                    </Col>
                </Row>

                {/* Action Buttons - Footer fuori dal Row */}
                <div style={{
                    marginTop: '1.5rem',
                    display: 'flex',
                    justifyContent: 'flex-end',
                    paddingTop: '1rem',
                    width: '100%'
                }}>
                    <PrimaryButton
                        onClick={handleSave}
                        disabled={!hasChanges.current || loading}
                        loading={loading}
                    >
                        Rearrange
                    </PrimaryButton>
                </div>
            </Modal>

            <ConfirmDiscardModal
                visible={showConfirmDiscard}
                onConfirm={handleConfirmDiscard}
                onCancel={handleCancelDiscard}
                mode={mode}
            />

            <ConfirmRearrangeModal
                visible={showConfirmRearrange}
                onConfirm={handleConfirmRearrange}
                onCancel={handleCancelRearrange}
                loading={loading}
                mode={mode}
            />
        </>
    );
};

export default ReorderModal; 